import api from './api';
import type { PaginatedResponse } from '@/types/admin';

/** Mirrors `InsurerEligibilityRule` (backend/apps/insurers/models_eligibility.py) field for field. */
export type EligibilityRuleType = 'REJECT' | 'ALLOW';
export type EligibilityManagedBy = 'insurer_admin' | 'country_admin' | 'broker_admin';
export type EligibilityVehicleType = 'TWO_WHEELER' | 'PRIVATE_CAR' | 'COMMERCIAL' | 'all';
export type EligibilityProductVariant = 'comprehensive' | 'tp_only' | 'saod' | 'all';
export type EligibilityChannel = 'broker' | 'corporate' | 'all';

export interface EligibilityRule {
  id: string;
  insurer: string;
  insurer_name?: string;
  rule_type: EligibilityRuleType;
  managed_by: EligibilityManagedBy;
  channel_type?: EligibilityChannel;
  broker?: string | null;
  vehicle_type?: EligibilityVehicleType;
  product_variant?: EligibilityProductVariant;
  vehicle_make?: string;
  vehicle_model?: string;
  /** State id (UUID); `state_name` is the read-only label. */
  state?: string | null;
  state_name?: string | null;
  city?: string;
  rto_code?: string;
  pincode?: string;
  min_cubic_capacity?: number | null;
  max_cubic_capacity?: number | null;
  min_seating_capacity?: number | null;
  max_seating_capacity?: number | null;
  min_vehicle_age_years?: number | null;
  max_vehicle_age_years?: number | null;
  min_idv?: string | number | null;
  max_idv?: string | number | null;
  max_discount_percent?: string | number | null;
  reason?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export type MasterType =
  | 'state'
  | 'rto'
  | 'previous_insurer'
  | 'city'
  | 'pincode'
  | 'fuel_type'
  | 'vehicle_make'
  | 'vehicle_model'
  | 'vehicle_variant'
  | 'vehicle_body_type';

export interface MasterMapping {
  /** null for an unmapped platform record (`mapping_status=unmapped`). */
  id: string | null;
  insurer: string;
  insurer_name?: string;
  insurer_platform_code?: string;
  master_type: MasterType;
  platform_record_id: string;
  platform_record_name?: string;
  platform_code?: string;
  /** The record's parent as a person reads it, e.g. the make of a model. */
  parent_name?: string;
  /** The insurer's own code for the platform record. */
  insurer_code: string;
  is_active: boolean;
}

/** One validated file row, as the server would (or did) apply it. */
export interface MasterMappingImportRow {
  row: number;
  insurer_code: string;
  insurer_name: string;
  master_type: MasterType;
  platform_code: string;
  platform_name: string;
  mapped_code: string;
  is_active: boolean;
  action: 'create' | 'update' | 'unchanged';
}

export interface MasterMappingImportResult {
  dry_run: boolean;
  total: number;
  imported: number;
  created: number;
  updated: number;
  unchanged: number;
  failed: number;
  errors: { row: number; errors: Record<string, string[] | string> }[];
  rows: MasterMappingImportRow[];
}

export interface EligibilityRulesParams {
  insurer?: string;
  rule_type?: string;
  vehicle_type?: string;
  state?: string;
  managed_by?: string;
  is_active?: boolean;
  page?: number;
  page_size?: number;
}

export interface MasterMappingsParams {
  insurer?: string;
  master_type?: string;
  line_of_business?: string;
  mapping_status?: string;
  data_health?: string;
  /** Vehicle parent filters: a make, then one of its models. */
  make?: string;
  model?: string;
  page?: number;
  page_size?: number;
}

export interface BulkImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors?: string[];
}

/** The facts the rules engine matches on (`apps.integrations.rules_engine`). */
export interface CheckEligibilityParams {
  vehicle_type?: string;
  product_variant?: string;
  vehicle_make?: string;
  vehicle_model?: string;
  state_id?: string;
  city?: string;
  rto_code?: string;
  pincode?: string;
  cubic_capacity?: number;
  seating_capacity?: number;
  vehicle_age_years?: number;
  idv?: number;
}

export interface CheckEligibilityRequest {
  insurer_id: string;
  broker_id?: string;
  params: CheckEligibilityParams;
}

export interface CheckEligibilityResult {
  eligible: boolean;
  reason?: string;
  max_discount_percent?: string | number | null;
}

export const eligibilityService = {
  // Eligibility Rules
  async getRules(params?: EligibilityRulesParams): Promise<PaginatedResponse<EligibilityRule>> {
    const response = await api.get('/insurers/eligibility-rules/', { params });
    return response.data;
  },

  async createRule(data: Partial<EligibilityRule>): Promise<EligibilityRule> {
    const response = await api.post('/insurers/eligibility-rules/', data);
    return response.data;
  },

  async updateRule(id: string, data: Partial<EligibilityRule>): Promise<EligibilityRule> {
    const response = await api.patch(`/insurers/eligibility-rules/${id}/`, data);
    return response.data;
  },

  async deleteRule(id: string): Promise<void> {
    await api.delete(`/insurers/eligibility-rules/${id}/`);
  },

  async bulkImportRules(data: { rules: Partial<EligibilityRule>[] }): Promise<BulkImportResult> {
    const response = await api.post('/insurers/eligibility-rules/bulk/', data);
    return response.data;
  },

  async checkEligibility(data: CheckEligibilityRequest): Promise<CheckEligibilityResult> {
    const response = await api.post('/insurers/eligibility-rules/check/', data);
    return response.data;
  },

  // Master Mappings
  async getMappings(params?: MasterMappingsParams): Promise<PaginatedResponse<MasterMapping>> {
    const response = await api.get('/insurers/master-mappings/', { params });
    return response.data;
  },

  /**
   * Upload a CSV/XLSX of mappings. `dryRun` validates and previews without
   * writing; `masterType` (the type picked on the page) refuses rows of any
   * other type.
   */
  async importMappings(file: File, dryRun: boolean, masterType?: string): Promise<MasterMappingImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('dry_run', dryRun ? 'true' : 'false');
    if (masterType) formData.append('master_type', masterType);
    const response = await api.post('/insurers/master-mappings/import/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * `kind` is `import-template` (header only, laid out for `master_type` when
   * given) or `export` (filtered rows).
   */
  async downloadMappingsFile(
    kind: 'import-template' | 'export',
    fileFormat: 'csv' | 'xlsx',
    params?: Record<string, string>,
  ): Promise<void> {
    const response = await api.get(`/insurers/master-mappings/${kind}/`, {
      params: { file_format: fileFormat, ...(params || {}) },
      responseType: 'blob',
    });
    const disposition = response.headers?.['content-disposition'] as string | undefined;
    const name = disposition?.match(/filename="?([^"]+)"?/)?.[1]
      || `master_mappings${kind === 'import-template' ? '_template' : ''}.${fileFormat}`;
    const href = URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = href;
    a.download = name;
    a.click();
    URL.revokeObjectURL(href);
  },
};

export default eligibilityService;
